// src/utils/qrOnlyBenefit.js
//
// Mirror ของ backend/utils/qrOnlyBenefit.js (เฉพาะส่วนตัดสินใจสถานะจาก state ที่มีอยู่แล้ว — ไม่รวม
// state machine ฝั่งเขียน Firestore ซึ่งอยู่ backend เท่านั้น ที่นี่แค่ "อ่าน" มาแสดงผล) ใช้ร่วมกัน
// ระหว่าง PaymentSettingsModal.vue และ QrOnlyBenefitCard.vue กันสอง component คำนวณ logic เพี้ยนไป
// คนละทางกัน (เคยเกิดมาแล้วครั้งนึง — PaymentSettingsModal เดิมเช็คจาก field ที่บันทึกไว้ใน Firestore
// อย่างเดียว ทำให้ร้านที่ตั้งค่า QR-only ไว้ก่อนฟีเจอร์นี้ deploy แล้วไม่เคยกลับมาบันทึกซ้ำ ไม่เห็น
// banner ทั้งที่ backend (isQrOnlyFreeAiChatEligible) ให้สิทธิ์ฟรีจริงอยู่แล้วจากการเช็คสด)

/**
 * 🚨 ปรับใหม่ — ตัด grace period ออกแล้วตามที่ตกลงกัน (เดิมมี "grace" เป็นสถานะกลางตอนร้านเพิ่งเปิด COD
 * กลับมา ยังฟรีต่ออีก 7 วัน) ตอนนี้เหลือแค่ 2 สถานะ: เข้าเงื่อนไข = "active" ไม่เข้า = "inactive" ทันที
 * @param {string[]} enabledMethods
 * @param {boolean} chargesEnabled
 * @returns {"active"|"inactive"}
 */
export function computeQrOnlyBenefitStatus(enabledMethods, chargesEnabled) {
    const liveEligible = Array.isArray(enabledMethods)
        && enabledMethods.length === 1
        && enabledMethods[0] === "qr_code"
        && chargesEnabled === true;
    return liveEligible ? "active" : "inactive";
}

// ═══════════════════════════════════════════════════════════════════════════
// ✅ ใหม่ — Free-message pool (mirror ของ backend/utils/qrOnlyBenefit.js ฝั่งอ่านอย่างเดียว)
// ═══════════════════════════════════════════════════════════════════════════
//
// qrFreePool มาจาก subscription/{pageId} doc ผ่าน subscriptionModule.js (มี Firestore realtime
// listener อยู่แล้ว — ดู CreditCard.vue: mounted() ที่ dispatch listenSubscriptionRealtime ไว้ตั้งแต่
// ต้น ถ้า component อยู่ในหน้า dashboard เดียวกัน ข้อมูลจะ sync เข้ามาเองไม่ต้อง dispatch ซ้ำ) — ไม่ต้อง
// เพิ่ม backend endpoint ใหม่เลยสำหรับเรื่องนี้

export const QR_FREE_POOL_CAP = 300; // mirror ของ backend/utils/qrOnlyBenefit.js — ต้องแก้คู่กันเสมอถ้าปรับตัวเลขนี้
const QR_FREE_POOL_LOW_THRESHOLD = 0.2; // 20% ของ cap — ต่ำกว่านี้ถือว่า "ใกล้หมด"
// ✅ ใหม่ — mirror ของ QR_FREE_POOL_RESET_INTERVAL_MS ฝั่ง backend (utils/qrOnlyBenefit.js) ต้องแก้คู่
// กันเสมอถ้าปรับตัวเลขนี้ — ใช้คำนวณ "อีกกี่วันจะรีเซ็ตอัตโนมัติ (fallback)" โชว์ให้ร้านเห็นตอนโควตาหมด
const QR_FREE_POOL_RESET_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000; // 30 วัน
// ✅ ใหม่ — mirror ของ backend/utils/qrOnlyBenefit.js (ต้องแก้คู่กันเสมอ)
// ราคาขั้นต่ำของสินค้าที่เปิดขายผ่าน QR (บาท) · ร้านที่ยังไม่เคยขายสำเร็จรีเซ็ตตามรอบ 30 วันได้กี่ครั้ง
// ค่าเริ่มต้นของทุกร้าน — แอดมินปรับรายร้านได้ (ค่าจริงของร้านมาจาก GET /pages/:id/qr-readiness → minPrice) ใช้ค่านี้เป็น fallback ก่อนโหลดเสร็จ
export const MIN_SELLABLE_PRICE = 20;
export const MIN_QR_SALE_AMOUNT = 20; // ยอดชำระรวม (บาท) ของออเดอร์ QR ที่นับว่า "ขายได้" เพื่อเติมโควตา
export const QR_NO_SALE_PERIODIC_RESET_LIMIT = 3;

/**
 * แปลง qrFreePool ดิบจาก subscription doc เป็นข้อมูลที่ template ใช้ได้ง่าย
 * @param {{remaining?: number, lastResetAt?: number, cap?: number}|null|undefined} qrFreePool
 * @param {number} [now]
 * @returns {{remaining: number, cap: number, isLow: boolean, isEmpty: boolean, daysUntilReset: number}}
 */
export function describeQrFreePool(qrFreePool, now = Date.now()) {
    // ✅ ใหม่ — cap ไม่ใช่ค่าคงที่ตายตัวทั้งระบบอีกต่อไป แอดมินปรับรายร้านได้แล้ว (ดู backend/utils/
    // qrOnlyBenefit.js: setQrFreePoolQuota + backend/routes/api/admin/shops.js) — ต้องอ่านจาก
    // qrFreePool.cap ที่ sync มาจาก subscription doc ก่อนเสมอ ถ้ายังไม่มี (ร้านเก่าที่ยังไม่เคยถูก
    // แอดมินปรับ หรือยังไม่ทัน sync) ค่อย fallback เป็น QR_FREE_POOL_CAP default — mirror ของ
    // getEffectiveCap ฝั่ง backend
    const cap = typeof qrFreePool?.cap === "number" && qrFreePool.cap > 0 ? qrFreePool.cap : QR_FREE_POOL_CAP;
    // ยังไม่เคยมี pool เลย (ร้านเพิ่งเข้าเงื่อนไข ยังไม่ทัน sync จาก backend) ถือว่าเต็ม cap ไว้ก่อน —
    // ตรงกับ getEffectiveQrFreePool ฝั่ง backend ที่ถือว่า "ยังไม่มี pool" = เต็ม
    const remaining = typeof qrFreePool?.remaining === "number" ? qrFreePool.remaining : cap;
    // ✅ ใหม่ — subscriptionModule.js listen ทั้ง doc แบบ generic (onSnapshot คืน snap.data() เต็มก้อน)
    // qrFreePool.lastResetAt ที่ backend เขียนไว้อยู่แล้ว (getEffectiveQrFreePool/refillQrFreePool) เลย
    // sync มาถึง frontend อยู่แล้วโดยไม่ต้องแก้อะไรฝั่ง backend/endpoint เพิ่ม — คำนวณเป็นจำนวนวันที่เหลือ
    // ก่อนถึงรอบ fallback reset ถัดไป ปัดขึ้นเสมอ (เหลือไม่ถึง 1 วันก็ยังโชว์เป็น "1 วัน" ไม่ใช่ 0)
    const lastResetAt = typeof qrFreePool?.lastResetAt === "number" ? qrFreePool.lastResetAt : now;
    const msUntilReset = Math.max(0, QR_FREE_POOL_RESET_INTERVAL_MS - (now - lastResetAt));
    const daysUntilReset = Math.ceil(msUntilReset / (24 * 60 * 60 * 1000));

    return {
        remaining,
        cap,
        isEmpty: remaining <= 0,
        isLow: remaining > 0 && remaining <= cap * QR_FREE_POOL_LOW_THRESHOLD,
        daysUntilReset,
        // ✅ ใหม่ — ร้านที่ไม่เคยขายสำเร็จเลยและใช้สิทธิ์รีเซ็ตตามรอบครบแล้ว: pool จะไม่รีเซ็ตเองอีก ต้องมียอดขาย QR ก่อน
        needsSaleToReset: qrFreePool?.everSold !== true
            && (Number(qrFreePool?.periodicResets) || 0) >= QR_NO_SALE_PERIODIC_RESET_LIMIT,
    };
}